import { AccessoryProductType } from 'data/types';

export type PlenumOpeningDimensionsType = {
  widthIn: number;
  heightIn: number;
};

export type RequiredPlenumDimensionsType = {
  supply: PlenumOpeningDimensionsType;
  return: PlenumOpeningDimensionsType;
};

export type ZoneDuctPlenumNeedType = {
  supply: PlenumOpeningDimensionsType | null;
  return: PlenumOpeningDimensionsType | null;
};

export type PlenumFitStatusType = 'confirmed' | 'unverified' | 'insufficient';

export type PlenumOpeningsProductType = {
  supplyPlenumWidthIn?: number | null;
  supplyPlenumHeightIn?: number | null;
  returnPlenumWidthIn?: number | null;
  returnPlenumHeightIn?: number | null;
};

const PLENUM_UNDERSIZE_TOLERANCE_IN = 0.125;
const PLENUM_OVERSIZE_TOLERANCE_IN = 1.5;

function positiveOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null;
}

export function requiredPlenumDimensionsForProduct(
  product: PlenumOpeningsProductType | null | undefined
): RequiredPlenumDimensionsType | null {
  const supplyWidthIn = positiveOrNull(product?.supplyPlenumWidthIn);
  const supplyHeightIn = positiveOrNull(product?.supplyPlenumHeightIn);
  const returnWidthIn = positiveOrNull(product?.returnPlenumWidthIn);
  const returnHeightIn = positiveOrNull(product?.returnPlenumHeightIn);
  if (
    supplyWidthIn === null ||
    supplyHeightIn === null ||
    returnWidthIn === null ||
    returnHeightIn === null
  ) {
    return null;
  }
  return {
    supply: { widthIn: supplyWidthIn, heightIn: supplyHeightIn },
    return: { widthIn: returnWidthIn, heightIn: returnHeightIn }
  };
}

export function parsePlenumDimensions(
  raw: string | null | undefined
): PlenumOpeningDimensionsType | null {
  if (!raw) return null;
  const match = /^(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)$/i.exec(raw.trim());
  if (!match) return null;
  return { widthIn: Number(match[1]), heightIn: Number(match[2]) };
}

function dimensionFits(kitIn: number, requiredIn: number): boolean {
  return (
    kitIn >= requiredIn - PLENUM_UNDERSIZE_TOLERANCE_IN &&
    kitIn <= requiredIn + PLENUM_OVERSIZE_TOLERANCE_IN
  );
}

function openingFits(
  kit: PlenumOpeningDimensionsType,
  required: PlenumOpeningDimensionsType
): boolean {
  return (
    dimensionFits(kit.widthIn, required.widthIn) &&
    dimensionFits(kit.heightIn, required.heightIn)
  );
}

export function plenumKitDimensionMatch(
  kit: AccessoryProductType,
  required: RequiredPlenumDimensionsType
): boolean | null {
  const supply = parsePlenumDimensions(
    kit.additionalInfo?.supplyPlenumDimensions
  );
  const returnDims = parsePlenumDimensions(
    kit.additionalInfo?.returnPlenumDimensions
  );
  if (!supply || !returnDims) return null;
  return (
    openingFits(supply, required.supply) &&
    openingFits(returnDims, required.return)
  );
}

export function plenumNeedForFit(
  plenumFit: PlenumFitStatusType | undefined,
  requiredPlenumDims: RequiredPlenumDimensionsType | null | undefined
): ZoneDuctPlenumNeedType | null {
  if (plenumFit !== 'insufficient') return null;
  return {
    supply: requiredPlenumDims?.supply ?? null,
    return: requiredPlenumDims?.return ?? null
  };
}
